/**
 * Dynamic Section Renderer Component
 *
 * Routes to the appropriate section renderer based on section type.
 * Currently supports:
 * - 'form': DynamicFormSection
 * - 'table': DynamicTableSection
 * - 'widget': DynamicWidgetSection
 * - 'activity': ActivityTabWithModals
 * - 'related-list': Placeholder (coming soon)
 */

import React from 'react';
import { TabSection, ActivitySectionConfig } from '../../types/tabConfiguration';
import DynamicFormSection from './sections/DynamicFormSection';
import DynamicTableSection from './sections/DynamicTableSection';
import DynamicWidgetSection from './sections/DynamicWidgetSection';
import { ActivityTabWithModals } from '../detail/ActivityTabWithModals';
import { Link } from 'lucide-react';
import { RecordType } from '../../types/activity';

interface DynamicSectionRendererProps {
  /** Section configuration from tab configuration */
  section: TabSection;
  /** Optional record ID for data fetching */
  recordId?: string;
  /** Record data for form sections (avoids mock when provided) */
  recordData?: Record<string, unknown>;
  /** Optional custom class name */
  className?: string;
  /** Callback when View Audit Details action is triggered */
  onViewAuditDetails?: (auditId: string) => void;
  /** Contact name for activity modals */
  contactName?: string;
  /** Phone number for call modal */
  phoneNumber?: string;
  /** Related record name for meeting modal */
  relatedToName?: string;
  /** Record type for activity tab */
  recordType?: RecordType;
  /** Hide section description text under section titles */
  hideSectionDescription?: boolean;
  /** Callback when a field is saved. Optional return value for parent use; the form keeps the edited value on success. */
  onFieldSave?: (fieldName: string, value: unknown) => void | Promise<unknown>;
}

/**
 * Dynamic Section Renderer
 *
 * Routes to the correct renderer component based on section.type
 */
const DynamicSectionRenderer: React.FC<DynamicSectionRendererProps> = ({
  section,
  recordId,
  recordData,
  className = '',
  onViewAuditDetails,
  contactName,
  phoneNumber,
  relatedToName,
  recordType = 'client',
  hideSectionDescription = false,
  onFieldSave,
}) => {
  const effectiveSection = hideSectionDescription
    ? { ...section, description: '' }
    : section;

  // Route based on section type
  switch (effectiveSection.type) {
    case 'form':
      return (
        <DynamicFormSection
          section={effectiveSection}
          recordId={recordId}
          recordData={recordData}
          className={className}
          onFieldSave={onFieldSave}
        />
      );

    case 'table':
      return (
        <DynamicTableSection
          section={effectiveSection}
          recordId={recordId}
          className={className}
          onViewAuditDetails={onViewAuditDetails}
        />
      );

    case 'widget':
      return (
        <DynamicWidgetSection
          section={effectiveSection}
          recordId={recordId}
          className={className}
        />
      );

    case 'activity': {
      const activityConfig = effectiveSection.config as ActivitySectionConfig;
      const effectiveRecordType = (activityConfig?.recordType as RecordType) || recordType;

      return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
          <ActivityTabWithModals
            recordId={recordId}
            recordType={effectiveRecordType}
            contactName={contactName}
            phoneNumber={phoneNumber}
            relatedToName={relatedToName}
          />
        </div>
      );
    }

    case 'related-list':
      return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
          <div className="flex items-start">
            <Link className="h-8 w-8 text-green-500 mr-4 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {effectiveSection.label}
              </h3>
              {effectiveSection.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {effectiveSection.description}
                </p>
              )}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-sm text-green-800 dark:text-green-200 font-medium mb-2">
                  Related List Section - Coming Soon
                </p>
                <p className="text-xs text-green-600 dark:text-green-300">
                  This section will display related records with inline actions and quick view.
                </p>
              </div>
            </div>
          </div>
        </div>
      );

    default:
      // Fallback for unknown section types
      return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-2">
              Unknown Section Type
            </p>
            <p className="text-xs text-yellow-600 dark:text-yellow-300">
              Section type "{section.type}" is not recognized. Please check your configuration.
            </p>
          </div>
        </div>
      );
  }
};

export default DynamicSectionRenderer;
